import React, { useEffect, useState } from 'react';
import { API_URL } from '../Config';
import {toast} from 'sonner';
import {
	Box,
	Typography,
	TextField,
	Select,
	MenuItem,
	FormControl,
	Checkbox,
	FormControlLabel,
	Radio,
	RadioGroup,
	Button,
	Paper,
	Grid2
} from '@mui/material';
import '../common/commonStyles.css';
import { getCompanySettings, clearCompanySettingsCache } from '../../utils/companySettingsCache';

export default function CompanySettings() {
	const accessToken = localStorage.getItem("accessToken");
	const token = localStorage.getItem("token");
	const [settings, setSettings] = useState({
		product_list_sorting: 'code',
		product_type: 'box',
		invoice_discount: 'Y',
		add_new_customer: 'Y',
		maxDiscountAllowedOnInvoice: '100',
		checkStockBeforeAddToCart: 'N',
		displayProductStock: 'Y',
		displayProductWithZeroStock: 'Y',
		canAddHalfBoxWhileOrder: 'N',
		showCustomerNameInPicking: 'Y',
		locationAccessPermissionMessage: 'N',
		canShareInvoice: 'Y',
		grnExpiryDateIsMandatory: 'Y',
		grnAcceptExcessQuantity: 'N',
		isSingleOrderPicking: 'N',
		isAutogenProductCode: 'N',
		enableDsr: 'Y',
		shopifyConnectivity: 'N',
		isShopifyVatInclusiveOrExclusive: '',
		showEmailTemplate: 'Y',
		amazonConnectivity: 'N',
		isAmazonVatInclusiveOrExclusive: 'N',
		QuickBooksConnectivity: 'no',
		taxType: 'VAT',
		currencySymbol: '£',
		timezone: 'Europe/London',
		showContraPrice: 'Y',
		isShowRutePlan: 'N',
		showCessRate: 'N',
		howmanyInvoiceCanPrint: 1,
	});

	const [orderFlow, setOrderFlow] = useState('1');
	const [orderSequence, setOrderSequence] = useState('1');

	const getValidSequenceOptions = (flow) => {
		if (flow === '3') return ['1', '2', '3'];
		if (flow === '2') return ['1', '2', '4'];
		return ['1', '2'];
	};

	const handleSettingChange = (e) => {
		const { name, value, type, checked } = e.target;

		if (name === 'howmanyInvoiceCanPrint') {
			const numValue = value === '' ? null : parseInt(value) || 0;
			setSettings(prev => ({ ...prev, [name]: numValue }));
		} else if (name === 'QuickBooksConnectivity' && type === 'checkbox') {
			setSettings(prev => ({
				...prev,
				[name]: checked ? 'yes' : 'no'
			}));
		} else {
			setSettings(prev => ({
				...prev,
				[name]: type === 'checkbox' ? (checked ? 'Y' : 'N') : value
			}));
		}
	};

	const handleFlowChange = (e) => {
		const newFlow = e.target.value;
		setOrderFlow(newFlow);
		
		if (newFlow === '3') {
			setOrderSequence('3');
		} else if (newFlow === '1') {
			setOrderSequence('1');
		} else {
			const validOptions = getValidSequenceOptions(newFlow);
			if (!validOptions.includes(orderSequence)) {
				setOrderSequence(validOptions[0]);
			}
		}
	};

	const handleSequenceChange = (e) => {
		setOrderSequence(e.target.value);
	};

	const loadSettings = async () => {
		try {
			const data = await getCompanySettings();

			if (data) {
				if (data.howmanyInvoiceCanPrint !== undefined) {
					const copiesValue = parseInt(data.howmanyInvoiceCanPrint);
					data.howmanyInvoiceCanPrint = isNaN(copiesValue) || copiesValue <= 0 ? null : copiesValue;
				}

				if (data.QuickBooksConnectivity != null) {
					localStorage.setItem(
						"QuickBooksConnectivity",
						String(data.QuickBooksConnectivity)
					);
				}

				setSettings(data);

				const flow = data.order_status ? String(data.order_status) : '1';
				const sequence = data.order_process_sequence ? String(data.order_process_sequence) : '1';
				
				setOrderFlow(flow);
				setOrderSequence(sequence);
			}
		} catch (error) {
			console.error('Failed to load settings:', error);
		}
	};

	useEffect(() => {
		loadSettings();
	}, []);

	const handleSubmit = async (e) => {
		e.preventDefault();

		try {
			const submitData = {
				...settings,
				howmanyInvoiceCanPrint: settings.howmanyInvoiceCanPrint || null,
				order_process_sequence: orderSequence,
				order_status: orderFlow
			};

			const authToken = token;
			const response = await fetch(`${API_URL}/settings/ws-edit-company-settings`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${authToken}`,
				},
				body: JSON.stringify(submitData),
			});

			const data = await response.json();

			if (data.success) {
				clearCompanySettingsCache();
				await loadSettings();
				localStorage.setItem(
					"QuickBooksConnectivity",
					settings.QuickBooksConnectivity || "no"
				);
				localStorage.setItem(
					"shopifyConnectivity",
					settings.shopifyConnectivity || "N"
				);
				localStorage.setItem(
					"amazonConnectivity",
					settings.amazonConnectivity || "N"
				);
				toast.success('Company settings updated successfully.');
			} else {
				toast.error(data.message || 'Failed to save settings');
			}
		} catch (error) {
			toast.error('An unexpected error occurred while saving settings.');
		}
	};

	const getSequenceLabel = (value) => {
		const labels = {
			'1': 'Picking First (Picking → Route Planning)',
			'2': 'Route Planning First (Route Planning → Picking)',
			'3': 'No Picking and No Route Planning',
			'4': 'Manifest Only'
		};
		return labels[value] || labels['1'];
	};

	const renderSequenceOptions = () => {
		const options = getValidSequenceOptions(orderFlow);
		return options.map(opt => (
			<MenuItem key={opt} value={opt}>{getSequenceLabel(opt)}</MenuItem>
		));
	};

	return (
		<Box className="page-container">
		
				<form onSubmit={handleSubmit}>
					<Paper
				       	className="paper-style"
					>
						<Box
							sx={{
								backgroundColor: '#EAEAEA',
								borderBottom: '1px solid rgba(48, 49, 51, 0.42)',
								p: 1.5,
							}}
						>
							<Grid2 container spacing={2}>
								<Grid2 size={{ xs: 12, md: 3 }}>
									<Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#212B36' }}>
										Module Name
									</Typography>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 9 }}>
									<Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#212B36' }}>
										Settings
									</Typography>
								</Grid2>
							</Grid2>
						</Box>

						<Box
							sx={{
								borderBottom: '1px dashed rgba(145, 158, 171, 0.16)',
								p: 1.5,
								'&:hover': {
									backgroundColor: 'rgba(0, 0, 0, 0.02)',
								},
							}}
						>
							<Grid2 container spacing={2}>
								<Grid2 size={{ xs: 12, md: 3 }}>
									<Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#212B36' }}>
										Product Settings
									</Typography>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 9 }}>
									<Grid2 container spacing={2}>
										<Grid2 size={{ xs: 12, md: 4 }}>
											<FormControlLabel
												control={
													<Checkbox
														name="isAutogenProductCode"
														checked={settings.isAutogenProductCode === 'Y'}
														onChange={handleSettingChange}
														size="small"
														className="checkBox"
													/>
												}
												label={
													<Typography variant="body2" sx={{ fontSize: '14px', color: '#637381' }}>
													 System Generate Product Codes?
													</Typography>
												}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 4 }}>
											<FormControlLabel
												control={
													<Checkbox
														name="displayProductStock"
														checked={settings.displayProductStock === 'Y'}
														onChange={handleSettingChange}
														size="small"
														className="checkBox"
													/>
												}
												label={
													<Typography variant="body2" sx={{ fontSize: '14px', color: '#637381' }}>
													 Displayed Stock to Staff?
													</Typography>
												}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 4 }}>
											<FormControlLabel
												control={
													<Checkbox
														name="displayProductWithZeroStock"
														checked={settings.displayProductWithZeroStock === 'Y'}
														onChange={handleSettingChange}
														size="small"
														className="checkBox"
													/>
												}
												label={
													<Typography variant="body2" sx={{ fontSize: '14px', color: '#637381' }}>
														Displayed Zero Stock?
													</Typography>
												}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 4 }}>
											<FormControlLabel
												control={
													<Checkbox
														name="checkStockBeforeAddToCart"
														checked={settings.checkStockBeforeAddToCart === 'Y'}
														onChange={handleSettingChange}
														size="small"
														className="checkBox"
													/>
												}
												label={
													<Typography variant="body2" sx={{ fontSize: '14px', color: '#637381' }}>
														Should Stock be Checked Before Adding to Cart?
													</Typography>
												}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 4 }}>
											<FormControlLabel
												control={
													<Checkbox
														name="showCessRate"
														checked={settings.showCessRate === 'Y'}
														onChange={handleSettingChange}
														size="small"
														className="checkBox"
													/>
												}
												label={
													<Typography variant="body2" sx={{ fontSize: '14px', color: '#637381' }}>
														Show Cess Rate?
													</Typography>
												}
											/>
										</Grid2>
									</Grid2>
								</Grid2>
							</Grid2>
						</Box>

						<Box
							sx={{
								borderBottom: '1px dashed rgba(145, 158, 171, 0.16)',
								p: 1.5,
								'&:hover': {
									backgroundColor: 'rgba(0, 0, 0, 0.02)',
								},
							}}
						>
							<Grid2 container spacing={2}>
								<Grid2 size={{ xs: 12, md: 3 }}>
									<Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#212B36' }}>
										Order Settings
									</Typography>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 9 }}>
									<Grid2 container spacing={2}>
										<Grid2 size={{ xs: 12, md: 4 }}>
											<FormControlLabel
												control={
													<Checkbox
														name="invoice_discount"
														checked={settings.invoice_discount === 'Y'}
														onChange={handleSettingChange}
														size="small"
														className="checkBox"
													/>
												}
												label={
													<Typography variant="body2" sx={{ fontSize: '14px', color: '#637381' }}>
														allow Staff to give Discounts on Invoices?
													</Typography>
												}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 4 }}>
											<FormControlLabel
												control={
													<Checkbox
														name="add_new_customer"
														checked={settings.add_new_customer === 'Y'}
														onChange={handleSettingChange}
														size="small"
														className="checkBox"
													/>
												}
												label={
													<Typography variant="body2" sx={{ fontSize: '14px', color: '#637381' }}>
														Add New Customers During Orders?
													</Typography>
												}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 4 }}>
											<FormControlLabel
												control={
													<Checkbox
														name="canAddHalfBoxWhileOrder"
														checked={settings.canAddHalfBoxWhileOrder === 'Y'}
														onChange={handleSettingChange}
														size="small"
														className="checkBox"
													/>
												}
												label={
													<Typography variant="body2" sx={{ fontSize: '14px', color: '#637381' }}>
														Allow Half Box to Order?
													</Typography>
												}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 4 }}>
											<FormControlLabel
												control={
													<Checkbox
														name="showCustomerNameInPicking"
														checked={settings.showCustomerNameInPicking === 'Y'}
														onChange={handleSettingChange}
														size="small"
														className="checkBox"
													/>
												}
												label={
													<Typography variant="body2" sx={{ fontSize: '14px', color: '#637381' }}>
														Should Customer Names be Shown During Picking?
													</Typography>
												}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 4 }}>
											<FormControlLabel
												control={
													<Checkbox
														name="isSingleOrderPicking"
														checked={settings.isSingleOrderPicking === 'Y'}
														onChange={handleSettingChange}
														size="small"
														className="checkBox"
													/>
												}
												label={
													<Typography variant="body2" sx={{ fontSize: '14px', color: '#637381' }}>
														Can Users Do Single Order Picking?
													</Typography>
												}
											/>
										</Grid2>
									</Grid2>

									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
										<Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#212B36', mb: 0.5 }}>
											Maximum Discount Allowed on Invoice (%)
										</Typography>
										<TextField
											className="input-border"
											type="number"
											placeholder="Enter percentage"
											name="maxDiscountAllowedOnInvoice"
											value={settings.maxDiscountAllowedOnInvoice}
											onChange={handleSettingChange}
											size="small"
											inputProps={{ min: 0, max: 100 }}
											sx={{ width: 150 }}
										/>
									</Box>
								</Grid2>
							</Grid2>
						</Box>

						<Box
							sx={{
								borderBottom: '1px dashed rgba(145, 158, 171, 0.16)',
								p: 1.5,
								'&:hover': {
									backgroundColor: 'rgba(0, 0, 0, 0.02)',
								},
							}}
						>
							<Grid2 container spacing={2}>
								<Grid2 size={{ xs: 12, md: 3 }}>
									<Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#212B36' }}>
										Order Flow Settings
									</Typography>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 9 }}>
									<Grid2 container spacing={2} sx={{ mt: 2 }}>
										<Grid2 size={{ xs: 12, md: 6 }}>
											<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
												<Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#212B36', mb: 0.5 }}>
													Standard Order Status Flow
												</Typography>
												<RadioGroup
													value={orderFlow}
													onChange={handleFlowChange}
													sx={{ 
														gap: 0.5,
														flexDirection: 'row'
													}}
												>
													<FormControlLabel
														value="1"
														control={<Radio size="small"
														  className="checkBox"
														/>}
														label={<Typography variant="body2" sx={{ fontSize: '14px', color: '#637381' }}>New</Typography>}
													/>
													<FormControlLabel
														value="2"
														control={<Radio size="small"
														  className="checkBox"
														/>}
														label={<Typography variant="body2" sx={{ fontSize: '14px', color: '#637381' }}>Perform Invoice</Typography>}
													/>
													<FormControlLabel
														value="3"
														control={<Radio size="small"
														  className="checkBox"
														/>}
														label={<Typography variant="body2" sx={{ fontSize: '14px', color: '#637381' }}>Delivered</Typography>}
													/>
												</RadioGroup>
											</Box>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 6 }}>
											<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
												<Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#212B36', mb: 0.5 }}>
													Order Processing Sequence
												</Typography>
												<FormControl size="small" sx={{ width: '100%', maxWidth: 400 }}>
													<Select
														
														value={orderSequence}
														onChange={handleSequenceChange}
														disabled={orderFlow !== '2'}
														renderValue={(value) => getSequenceLabel(value)}
														sx={{
															borderRadius: '8px',
															backgroundColor: orderFlow !== '2' ? '#F5F5F5' : '#F4F6F8',
															'& .MuiOutlinedInput-notchedOutline': {
																borderColor: 'rgba(145, 158, 171, 0.32)',
															},
															'&.Mui-disabled': {
																backgroundColor: '#F5F5F5',
																color: '#212B36',
																'& .MuiSelect-select': {
																	color: '#212B36',
																	WebkitTextFillColor: '#212B36',
																},
															},
														}}
													>
														{renderSequenceOptions()}
													</Select>
												</FormControl>
											</Box>
										</Grid2>
									</Grid2>
								</Grid2>
							</Grid2>
						</Box>

						<Box
							sx={{
								borderBottom: '1px dashed rgba(145, 158, 171, 0.16)',
								p: 1.5,
								'&:hover': {
									backgroundColor: 'rgba(0, 0, 0, 0.02)',
								},
							}}
						>
							<Grid2 container spacing={2}>
								<Grid2 size={{ xs: 12, md: 3 }}>
									<Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#212B36' }}>
										Integration Settings
									</Typography>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 9 }}>
									<Grid2 container spacing={2}>
										<Grid2 size={{ xs: 12, md: 4 }}>
											<FormControlLabel
												control={
													<Checkbox
														name="shopifyConnectivity"
														checked={settings.shopifyConnectivity === 'Y'}
														onChange={handleSettingChange}
														size="small"
														className="checkBox"
													/>
												}
												label={
													<Typography variant="body2" sx={{ fontSize: '14px', color: '#637381' }}>
														Shopify Integration?
													</Typography>
												}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 4 }}>
											<FormControlLabel
												control={
													<Checkbox
														name="amazonConnectivity"
														checked={settings.amazonConnectivity === 'Y'}
														onChange={handleSettingChange}
														size="small"
														className="checkBox"
													/>
												}
												label={
													<Typography variant="body2" sx={{ fontSize: '14px', color: '#637381' }}>
														Amazon Integration?
													</Typography>
												}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 4 }}>
											<FormControlLabel
												control={
													<Checkbox
														name="QuickBooksConnectivity"
														checked={String(settings.QuickBooksConnectivity || '').toLowerCase() === 'yes'}
														onChange={handleSettingChange}
														size="small"
														className="checkBox"
													/>
												}
												label={
													<Typography variant="body2" sx={{ fontSize: '14px', color: '#637381' }}>
														QuickBooks Integration?
													</Typography>
												}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 4 }}>
											<FormControlLabel
												control={
													<Checkbox
														name="enableDsr"
														checked={settings.enableDsr === 'Y'}
														onChange={handleSettingChange}
														size="small"
												        className="checkBox"
													/>
												}
												label={
													<Typography variant="body2" sx={{ fontSize: '14px', color: '#637381' }}>
														DSR (Direct Sales Representative) Feature Enabled?
													</Typography>
												}
											/>
										</Grid2>
									</Grid2>

									<Grid2 container spacing={2} sx={{ mt: 2 }}>
											<Grid2 size={{ xs: 12, md: 6 }}>
												<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
													<Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#212B36' }}>
														Shopify VAT Calculation Method
													</Typography>
													<FormControl size="small" fullWidth>
														<Select
															name="isShopifyVatInclusiveOrExclusive"
															value={settings.isShopifyVatInclusiveOrExclusive}
															onChange={handleSettingChange}
															displayEmpty
														
														>
															<MenuItem value="">Select VAT calculation method</MenuItem>
															<MenuItem value="Y">VAT Inclusive (Price includes VAT)</MenuItem>
															<MenuItem value="N">VAT Exclusive (VAT added to price)</MenuItem>
														</Select>
													</FormControl>
												</Box>
											</Grid2>
											<Grid2 size={{ xs: 12, md: 6 }}>
												<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
													<Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#212B36' }}>
														Amazon VAT Calculation Method
													</Typography>
													<FormControl size="small" fullWidth>
														<Select
															name="isAmazonVatInclusiveOrExclusive"
															value={settings.isAmazonVatInclusiveOrExclusive}
															onChange={handleSettingChange}
															displayEmpty
														
														>
															<MenuItem value="">Select VAT calculation method</MenuItem>
															<MenuItem value="Y">VAT Inclusive (Price includes VAT)</MenuItem>
															<MenuItem value="N">VAT Exclusive (VAT added to price)</MenuItem>
														</Select>
													</FormControl>
												</Box>
											</Grid2>
										</Grid2>
								</Grid2>
							</Grid2>
						</Box>

						<Box
							sx={{
								borderBottom: '1px dashed rgba(145, 158, 171, 0.16)',
								p: 1.5,
								'&:hover': {
									backgroundColor: 'rgba(0, 0, 0, 0.02)',
								},
							}}
						>
							<Grid2 container spacing={2}>
								<Grid2 size={{ xs: 12, md: 3 }}>
									<Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#212B36' }}>
										Invoice Settings
									</Typography>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 9 }}>
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
										<Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#212B36' }}>
											Number of Invoice Copies to Print
										</Typography>
										<TextField
											className="input-border"
											type="number"
											placeholder="Enter number (1-10, or leave empty for default)"
											name="howmanyInvoiceCanPrint"
											value={settings.howmanyInvoiceCanPrint || ''}
											onChange={handleSettingChange}
											size="small"
											inputProps={{ min: 1, max: 10 }}
											sx={{ width: 150 }}
										/>
									</Box>
								</Grid2>
							</Grid2>
						</Box>

						<Box
							sx={{
								p: 1.5,
								'&:hover': {
									backgroundColor: 'rgba(0, 0, 0, 0.02)',
								},
							}}
						>
							<Grid2 container spacing={2}>
								<Grid2 size={{ xs: 12, md: 3 }}>
									<Typography sx={{ fontWeight: 500, fontSize: '14px', color: '#212B36' }}>
										Other Settings
									</Typography>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 9 }}>
									<Grid2 container spacing={2}>
										<Grid2 size={{ xs: 12, md: 4 }}>
											<FormControlLabel
												control={
													<Checkbox
														name="showEmailTemplate"
														checked={settings.showEmailTemplate === 'Y'}
														onChange={handleSettingChange}
														size="small"
														className="checkBox"
													/>
												}
												label={
													<Typography variant="body2" sx={{ fontSize: '14px', color: '#637381' }}>
														 Email Templates?
													</Typography>
												}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 4 }}>
											<FormControlLabel
												control={
													<Checkbox
														name="showContraPrice"
														checked={settings.showContraPrice === 'Y'}
														onChange={handleSettingChange}
														size="small"
														className="checkBox"
													/>
												}
												label={
													<Typography variant="body2" sx={{ fontSize: '14px', color: '#637381' }}>
														 Contra Price feature?
													</Typography>
												}
											/>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 4 }}>
											<FormControlLabel
												control={
													<Checkbox
														name="isShowRutePlan"
														checked={settings.isShowRutePlan === 'Y'}
														onChange={handleSettingChange}
														size="small"
														className="checkBox"
													/>
												}
												label={
													<Typography variant="body2" sx={{ fontSize: '14px', color: '#637381' }}>
														 Route Planning feature?
													</Typography>
												}
											/>
										</Grid2>
									</Grid2>

									<Grid2 container spacing={2} sx={{ mt: 2 }}>
										<Grid2 size={{ xs: 12, md: 4 }}>
											<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
												<Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#212B36' }}>
													Tax Type (E.G., VAT, GST, Sales Tax)
												</Typography>
												<TextField
													className="input-border"
													type="text"
													placeholder="Enter tax type"
													name="taxType"
													value={settings.taxType}
													onChange={handleSettingChange}
													size="small"
													fullWidth
													
												/>
											</Box>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 4 }}>
											<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
												<Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#212B36' }}>
													Currency Symbol (E.G., £, $, €)
												</Typography>
												<TextField
													className="input-border"
													type="text"
													placeholder="Enter currency symbol"
													name="currencySymbol"
													value={settings.currencySymbol}
													onChange={handleSettingChange}
													size="small"
													fullWidth
											
												/>
											</Box>
										</Grid2>
										<Grid2 size={{ xs: 12, md: 4 }}>
											<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
												<Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#212B36' }}>
													Timezone (E.G., Europe/London, America/New_York)
												</Typography>
												<TextField
													className="input-border"
													type="text"
													placeholder="Enter timezone"
													name="timezone"
													value={settings.timezone}
													onChange={handleSettingChange}
													size="small"
													fullWidth
											
												/>
											</Box>
										</Grid2>
									</Grid2>
								</Grid2>
							</Grid2>
							<Box sx={{ borderTop: '1px solid rgba(145, 158, 171, 0.16)', my:1, py:1 }} display="flex" justifyContent="flex-end">
								<Button
									type="submit"
									variant="contained"
								   	className="button-variant-contained"
								>
									Save Settings
								</Button>
							</Box>
						</Box>
					</Paper>
				</form>
		
		</Box>
	);
}
